/**
 * Pure mapping from a persisted/extracted tool-result payload to a chat Message.
 *
 * The single source of truth for turning a `{kind, payload}` tool result into the
 * `Message` the UI renders — used by BOTH the live path (useMessageManagement's
 * insertToolResult, after it extracts the payload from the tool output) and the
 * restore path (useAssistantConnection's HISTORY_RESTORED, where the backend already
 * persisted the extracted payload). Sharing it guarantees a restored card is
 * byte-identical to the live one.
 *
 * `payload` is the EXTRACTED render shape:
 *   - COURSE_BROWSER → Course[] (the course list the card renders)
 *   - RAG_CONTENT    → RAGData  ({ summary, references: { references: [...] } })
 *
 * Returns null for unrenderable input (empty course list, RAG with no references,
 * unknown kind) — the same skip conditions the live path used, so empty results
 * never become a blank card.
 *
 * Pure (no React, no DOM) so it is trivially unit-testable.
 */

import { ComponentType, type Message } from '@/types/chat';
import type { Course } from '@/types/course';
import type { RAGData } from '@/types/rag';

export function mapPersistedResultToMessage(
  kind: string,
  payload: unknown,
  id: string
): Message | null {
  if (kind === ComponentType.COURSE_BROWSER) {
    const courses = payload as Course[];
    if (Array.isArray(courses) && courses.length > 0) {
      return {
        role: 'assistant',
        result: { kind: ComponentType.COURSE_BROWSER, courses },
        id,
      };
    }
    return null;
  }

  if (kind === ComponentType.RAG_CONTENT) {
    const ragContent = payload as Partial<RAGData>;
    if (
      ragContent &&
      typeof ragContent === 'object' &&
      'summary' in ragContent
    ) {
      const refs = ragContent.references?.references ?? [];
      // Empty refs is a deliberate skip — the assistant's own text answer carries
      // the content, so there is no card to render (matches the live path).
      if (refs.length === 0) return null;
      return {
        role: 'assistant',
        result: {
          kind: ComponentType.RAG_CONTENT,
          ragContent: ragContent as RAGData,
        },
        id,
      };
    }
    return null;
  }

  return null;
}
